import { Fragment } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import ContentWrapper from '~/tutor-on-demand/components/Content/ContentWrapper'
import HeaderTitle from '~/tutor-on-demand/components/Header/HeaderTitle'
import HeaderWrapper from '~/tutor-on-demand/components/Header/HeaderWrapper'
import List from '~/tutor-on-demand/components/Typography/List'
import TutorOnDemandProductCards from '~/shared/components/TutorOnDemandProductCards/TutorOnDemandProductCards.component'
import Paragraph from '~/tutor-on-demand/components/Typography/Paragraph'
import SubContainer from '~/tutor-on-demand/components/Content/SubContainer'
import SubContainerWrapper from '~/tutor-on-demand/components/Content/SubContainerWrapper'
import { SubjectType, getSubjectImage } from '~/components/Metadata/SubjectMetatags'

import { Breakpoint, useCheckBreakpoint } from '~/utils'
import ROUTES from '~/shared/constants/routes.const'
import BreadCrumbs from '~/encyclopedia/components/BreadCrumbs'

enum Topic {
  THE_PERIODIC_TABLE = 'the-periodic-table',
  ATOMIC_MODELS = 'atomic-models',
  TYPES_OF_BONDS = 'types-of-bonds',
  CALCULATIONS = 'calculations',
  THERMOCHEMISTRY = 'thermochemistry',
  LE_CHATELIERS_PRINCIPLE = 'le-chateliers-principle',
  INORGANIC_AND_ORGANIC_ANALYSIS = 'inorganic-and-organic-analysis',
  SEPARATION_TECHNIQUES = 'separation-techniques',
  BUFFERS = 'buffers',
  ELECTROCHEMISTRY = 'electrochemistry',
  FUNCTIONAL_GROUPS_AND_REACTION_TYPES = 'functional-groups-and-reaction-types',
  BIOCHEMISTRY = 'biochemistry',
  POLYMERS = 'polymers',
}

const chemistryLevels = ['Kjemi 1/REA 3011', 'Kjemi 2/REA 3012 og REA 3013']

export default function TutorOnDemandChemistry({ type }: { type: SubjectType }): React.ReactElement {
  const { t } = useTranslation(['tutorOnDemand', 'tutorOnDemandChemistry'])
  const breadcrumbs = [
    { path: ROUTES.TUTOR_ON_DEMAND_INFORMATION, h1: t('tutorOnDemand:landingPage.whatIsTutorOnDemand.title') },
    { path: ROUTES.TUTOR_ON_DEMAND_CHEMISTRY, h1: t('tutorOnDemandChemistry:title') },
  ]
  const { locale } = useRouter()
  const isSmallScreen = useCheckBreakpoint({ lt: Breakpoint.XL })

  return (
    <Fragment>
      <HeaderWrapper>
        <BreadCrumbs
          breadcrumbs={breadcrumbs}
          fontSize="medium"
          isWhite
          itemClassNames="font-medium items-center"
          wrapperClassNames="mt-1 pr-12 lg:pr-0"
        />

        <HeaderTitle
          iconAlt={getSubjectImage(type, t).alt as string}
          iconSrc={getSubjectImage(type, t).srcKey as string}
        >
          {t('tutorOnDemandChemistry:title')}
        </HeaderTitle>
      </HeaderWrapper>
      <ContentWrapper className="space-y-8">
        <div className="xl:space-x-8 xl:flex">
          <section className="mb-6 space-y-6 xl:mb-0">
            <Paragraph>{t('tutorOnDemandChemistry:contentParagraph1')}</Paragraph>
            <Paragraph>{t('tutorOnDemandChemistry:contentParagraph2')}</Paragraph>
            <Paragraph>{t('tutorOnDemandChemistry:contentParagraph3')}</Paragraph>
            {!isSmallScreen && (
              <TutorOnDemandProductCards
                className="pt-8 max-w-max space-y-14 lg:space-y-0 lg:space-x-4 xl:space-x-8 flex flex-col lg:flex-row"
                colorScheme="white"
                hideChatWithTutor
                hideVideoChatWithTutor
              />
            )}
          </section>
          <SubContainerWrapper>
            <SubContainer className="w-full max-w-sm" header={t('tutorOnDemandChemistry:weCanHelpYouWith.header')}>
              <List
                items={Object.values(Topic).map((item) => t(`tutorOnDemandChemistry:weCanHelpYouWith.items.${item}`))}
              />
            </SubContainer>
            {locale === 'no' && (
              <SubContainer className="w-full max-w-sm" header="Vi kan alle fag i kjemi, som for eksempel">
                <List items={chemistryLevels} />
              </SubContainer>
            )}
          </SubContainerWrapper>
        </div>
        {isSmallScreen && (
          <TutorOnDemandProductCards
            className="pt-8 max-w-max space-y-14 lg:space-y-0 lg:space-x-4 xl:space-x-8 flex flex-col lg:flex-row"
            colorScheme="white"
            hideChatWithTutor
            hideVideoChatWithTutor
          />
        )}
      </ContentWrapper>
    </Fragment>
  )
}
